import { useState, useCallback, useRef, useEffect } from 'react';
import { Message, ChatResponse, ConversationState } from '@/types';
import { chatApi, apiUtils } from '@/services/api';
import { getApiUrl } from '@/config';

interface UseChatOptions {
  autoStart?: boolean;
  maxMessages?: number;
  onError?: (error: string) => void;
  onMessageReceived?: (response: ChatResponse) => void;
}

export const useChat = (options: UseChatOptions = {}) => {
  const { autoStart = false, maxMessages = 100, onError, onMessageReceived } = options;
  
  const [conversation, setConversation] = useState<ConversationState>({
    id: null,
    messages: [],
    quotationData: {},
    dataCompletion: '0/6 complete',
    isLoading: false,
  });

  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initAttemptedRef = useRef(false);

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages, scrollToBottom]);

  // Start new conversation (local-only, no backend call needed for serverless)
  const startNewConversation = useCallback(async () => {
    try {
      setError(null);
      const newId = `conv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      setConversation({
        id: newId,
        messages: [
          {
            id: Date.now().toString(),
            content: "Hello! I'm Mary from PrintnGift. I'd love to help you find perfect corporate gifts! What's the occasion, and how many gift packs do you need?",
            isUser: false,
            timestamp: new Date(),
          }
        ],
        quotationData: {},
        dataCompletion: '0/6 complete',
        isLoading: false,
      });
    } catch (err) {
      const errorMsg = apiUtils.handleError(err);
      setError(errorMsg);
      onError?.(errorMsg);
      setConversation(prev => ({ ...prev, isLoading: false }));
    }
  }, [onError]);

  // Send message
  const sendMessage = useCallback(async (content: string, image?: string) => {
    if (!content.trim() && !image) return;

    try {
      setError(null);
      setConversation(prev => ({ ...prev, isLoading: true }));

      // Add user message immediately
      const userMessage: Message = {
        id: Date.now().toString(),
        content: content.trim(),
        isUser: true,
        timestamp: new Date(),
        image,
      };

      setConversation(prev => ({
        ...prev,
        messages: [...prev.messages, userMessage].slice(-maxMessages),
      }));

      // Build conversation history for the serverless backend
      const history = conversation.messages
        .filter(m => m.content)
        .map(m => ({
          role: m.isUser ? 'user' : 'assistant',
          content: m.content,
        }));

      // Prepare request
      const request = {
        message: content.trim(),
        conversation_id: conversation.id || undefined,
        history,
        ...(image && { image }),
      };

      // Send to API
      const response = await chatApi.sendMessage(request);

      // Get admin settings
      const quotationSettings = localStorage.getItem('quotationSettings');
      const parsedSettings = quotationSettings ? JSON.parse(quotationSettings) : {};
      const showDraftQuotation = parsedSettings.show_draft_quotation ?? true;
      const emailNotificationEnabled = parsedSettings.email_notification_enabled ?? true;

      // Create bot message
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response.response,
        isUser: false,
        timestamp: new Date(),
        products: showDraftQuotation ? response.products : undefined,
      };

      // Check if quotation is now complete and add thank you message
      const isQuotationNowComplete = response.data_completion?.includes('6/6') || 
                                    (response.quotation_data && 
                                     response.quotation_data.customer_email && 
                                     response.quotation_data.company_name && 
                                     (response.quotation_data.budget_per_pack || response.quotation_data.total_budget) &&
                                     response.quotation_data.number_of_packs &&
                                     response.quotation_data.occasion &&
                                     response.quotation_data.special_requests);

      // Create messages array
      let messagesToAdd = [botMessage];
      
      // Add thank you message if quotation is complete and email notifications are enabled
      if (isQuotationNowComplete && emailNotificationEnabled && response.quotation_data?.customer_email) {
        const thankYouMessage: Message = {
          id: (Date.now() + 2).toString(),
          content: `Thank you for providing all the details! I have everything needed to prepare your customized quotation. A formal quotation with detailed pricing, product specifications, and delivery information will be sent to ${response.quotation_data.customer_email} within 24 hours. \n\nIs there anything else I can help you with regarding your corporate gift requirements?`,
          isUser: false,
          timestamp: new Date(),
        };
        messagesToAdd.push(thankYouMessage);
      }

      // Update conversation state
      setConversation(prev => ({
        ...prev,
        id: response.conversation_id || prev.id,
        messages: [...prev.messages, ...messagesToAdd].slice(-maxMessages),
        quotationData: response.quotation_data || prev.quotationData,
        dataCompletion: response.data_completion || prev.dataCompletion,
        isLoading: false,
      }));

      onMessageReceived?.(response);

    } catch (err) {
      const errorMsg = apiUtils.handleError(err);
      setError(errorMsg);
      onError?.(errorMsg);
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        content: "I'm having trouble connecting to the server. Please try again later.",
        isUser: false,
        timestamp: new Date(),
      };

      setConversation(prev => ({
        ...prev,
        messages: [...prev.messages, errorMessage].slice(-maxMessages),
        isLoading: false,
      }));
    }
  }, [conversation.id, maxMessages, onError, onMessageReceived]);

  // Upload image
  const uploadImage = useCallback(async (file: File) => {
    try {
      setError(null);
      setConversation(prev => ({ ...prev, isLoading: true }));

      // Add user message with image
      const userMessage: Message = {
        id: Date.now().toString(),
        content: "",
        isUser: true,
        timestamp: new Date(),
        image: URL.createObjectURL(file),
      };

      setConversation(prev => ({
        ...prev,
        messages: [...prev.messages, userMessage].slice(-maxMessages),
      }));

      // Upload to API
      const response = await chatApi.uploadImage(file, conversation.id || undefined);

      // Get admin settings to check if products should be shown
      const uploadQuotationSettings = localStorage.getItem('quotationSettings');
      const showDraftQuotation = uploadQuotationSettings ? 
        JSON.parse(uploadQuotationSettings).show_draft_quotation ?? true : true;

      // Create bot message
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response.response,
        isUser: false,
        timestamp: new Date(),
        products: showDraftQuotation ? response.products : undefined,
      };

      // Update conversation state
      setConversation(prev => ({
        ...prev,
        id: response.conversation_id || prev.id,
        messages: [...prev.messages, botMessage].slice(-maxMessages),
        quotationData: response.quotation_data || prev.quotationData,
        dataCompletion: response.data_completion || prev.dataCompletion,
        isLoading: false,
      }));

      onMessageReceived?.(response);

    } catch (err) {
      const errorMsg = apiUtils.handleError(err);
      setError(errorMsg);
      onError?.(errorMsg);
      setConversation(prev => ({ ...prev, isLoading: false }));
    }
  }, [conversation.id, maxMessages, onError, onMessageReceived]);

  // Reset conversation (local-only for serverless)
  const resetConversation = useCallback(async () => {
    try {
      setError(null);

      // Clear local state
      setConversation({
        id: null,
        messages: [],
        quotationData: {},
        dataCompletion: '0/6 complete',
        isLoading: false,
      });

      // Reset init ref to allow new conversation
      initAttemptedRef.current = false;

      // Start fresh conversation
      await startNewConversation();

    } catch (err) {
      const errorMsg = apiUtils.handleError(err);
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [onError, startNewConversation]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load demo conversation with full message history
  const loadDemoConversation = useCallback(async (demoResponse: ChatResponse) => {
    try {
      setError(null);
      setConversation(prev => ({ ...prev, isLoading: true }));

      // First, load the initial demo response
      setConversation({
        id: demoResponse.conversation_id ?? null,
        messages: [], // Start with empty messages
        quotationData: demoResponse.quotation_data || {},
        dataCompletion: demoResponse.data_completion || '0/6 complete',
        isLoading: false,
      });

      // Now simulate the step-by-step conversation
      await simulateDemoConversation(demoResponse);

      onMessageReceived?.(demoResponse);

    } catch (err) {
      const errorMsg = apiUtils.handleError(err);
      setError(errorMsg);
      onError?.(errorMsg);
      setConversation(prev => ({ ...prev, isLoading: false }));
    }
  }, [onError, onMessageReceived]);

  // Get conversation steps based on demo response  
  const getConversationSteps = useCallback(async (demoResponse: ChatResponse) => {
    try {
      // Try to fetch the actual conversation messages from backend
      const conversationId = demoResponse.conversation_id || '';
      const scenarioMatch = conversationId.match(/demo_([^_]+)_/);
      const scenario = scenarioMatch ? scenarioMatch[1] : 'premium_christmas';
      
      // Fetch the demo scenario data from backend
      const response = await fetch(getApiUrl('/api/demo-scenarios'));
      if (response.ok) {
        const data = await response.json();
        const scenarioData = data.scenarios[scenario];
        
        if (scenarioData) {
          // Get the scenario details to build conversation
          return await getScenarioConversation(scenario);
        }
      }
    } catch (error) {
      console.error('Error fetching scenario data:', error);
    }
    
    // Fallback to default conversation - make sure it's a full conversation
    return await getScenarioConversation('premium_christmas');
  }, []);

  // Get scenario-specific conversation flow
  const getScenarioConversation = useCallback(async (scenario: string) => {
    if (scenario === 'premium_christmas') {
      return [
        {
          content: "Hi! I need corporate gifts for our company Christmas party.",
          isUser: true
        },
        {
          content: "Hello! I'm Mary from PrintnGift, and I'd love to help you find perfect Christmas corporate gifts! To provide the best recommendations, could you tell me about your company and how many people we're planning gifts for?",
          isUser: false
        },
        {
          content: "I'm Sarah from Tech Solutions Inc. We have 50 employees who will attend the party.",
          isUser: true
        },
        {
          content: "Wonderful, Sarah! Tech Solutions Inc sounds like an exciting company. For 50 employees, I can suggest some great options. What's your budget range per person for these Christmas gifts?",
          isUser: false
        },
        {
          content: "Our budget is $75 per person, so total budget of $3750.",
          isUser: true
        },
        {
          content: "Excellent! $75 per person gives us fantastic premium options. What email should I send the detailed quotation to?",
          isUser: false
        },
        {
          content: "Please send it to sarah.johnson@techsolutions.com",
          isUser: true
        },
        {
          content: "Perfect! Any special requirements for the gifts? For example, custom logo engraving, special packaging, or specific themes?",
          isUser: false
        },
        {
          content: "Yes, we'd like our company logo engraved on the items for branding.",
          isUser: true
        },
        {
          content: "Perfect! I have all the information needed. Here are some premium drinkware options ideal for Tech Solutions Inc's Christmas corporate gifts. All items can be customized with your company logo through professional engraving.",
          isUser: false,
          showProducts: true
        }
      ];
    } else if (scenario === 'budget_appreciation') {
      return [
        {
          content: "Hello, I'm looking for employee appreciation gifts.",
          isUser: true
        },
        {
          content: "Hi there! I'm Mary from PrintnGift. I'd love to help with employee appreciation gifts! How many team members are we planning gifts for?",
          isUser: false
        },
        {
          content: "We have 20 people in our team, and our budget is around $30 per person.",
          isUser: true
        },
        {
          content: "Perfect! $30 per person gives us great practical options. What's your company name and what's your contact email for the quotation?",
          isUser: false
        },
        {
          content: "I'm Mike from Creative Design Studio, and my email is mike@creativedesign.com.",
          isUser: true
        },
        {
          content: "Excellent, Mike! What's the specific occasion for these appreciation gifts?",
          isUser: false
        },
        {
          content: "It's for our quarterly employee appreciation event.",
          isUser: true
        },
        {
          content: "Great! Any special requirements? Custom branding, specific types of items?",
          isUser: false
        },
        {
          content: "We'd like practical items they can use daily, with our studio logo if possible.",
          isUser: true
        },
        {
          content: "Perfect! Here are some practical daily-use items perfect for Creative Design Studio's team appreciation. All options can be customized with your studio branding.",
          isUser: false,
          showProducts: true
        }
      ];
    } else {
      // executive_premium or default
      return [
        {
          content: "We need premium gifts for our executive retreat.",
          isUser: true
        },
        {
          content: "Wonderful! I'm Mary from PrintnGift, specializing in premium executive gifting. How many executives will be attending, and what's your budget range?",
          isUser: false
        },
        {
          content: "25 executives, budget is $150 per person.",
          isUser: true
        },
        {
          content: "Excellent! With $150 per person, we can create truly impressive packages. Which company should I prepare this quotation for, and what's the contact email?",
          isUser: false
        },
        {
          content: "This is for GlobalCorp International, contact: executive.assistant@globalcorp.com.",
          isUser: true
        },
        {
          content: "Perfect! What's the specific occasion, and any special requirements?",
          isUser: false
        },
        {
          content: "It's for our annual executive retreat. Looking for sophisticated items that reflect our premium brand.",
          isUser: true
        },
        {
          content: "Outstanding! Here's our premium executive collection, carefully selected for GlobalCorp International. Each item represents the sophistication and quality that matches your brand's excellence.",
          isUser: false,
          showProducts: true
        }
      ];
    }
  }, []);

  // Simulate typing conversation with delays
  const simulateTypingConversation = useCallback(async (demoResponse: ChatResponse) => {
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Get admin settings to check if products should be shown
    const demoQuotationSettings = localStorage.getItem('quotationSettings');
    const showDraftQuotation = demoQuotationSettings ? 
      JSON.parse(demoQuotationSettings).show_draft_quotation ?? true : true;
    
    // Define conversation steps based on scenario
    const conversationSteps = await getConversationSteps(demoResponse);
    
    for (let i = 0; i < conversationSteps.length; i++) {
      const step = conversationSteps[i];
      
      // Show typing indicator
      setConversation(prev => ({ ...prev, isLoading: true }));
      await delay(1000); // Typing delay
      
      // Add message
      const message: Message = {
        id: `${Date.now()}-${i}`,
        content: step.content,
        isUser: step.isUser,
        timestamp: new Date(),
        products: (step as any).showProducts && showDraftQuotation ? demoResponse.products : undefined,
      };

      setConversation(prev => ({
        ...prev,
        messages: [...prev.messages, message],
        isLoading: false,
        // Update quotation data progressively - show progress building up
        quotationData: i >= conversationSteps.length - 2 ? demoResponse.quotation_data || {} : prev.quotationData,
        dataCompletion: i >= conversationSteps.length - 2 ? demoResponse.data_completion || '0/6 complete' : `${Math.min(i, 5)}/6 complete`,
      }));

      // Delay before next message
      if (i < conversationSteps.length - 1) {
        await delay(1000); // 1 second delay for all messages
      }
    }
  }, [getConversationSteps]);

  // Simulate step-by-step demo conversation
  const simulateDemoConversation = useCallback(async (demoResponse: ChatResponse) => {
    // Get the conversation ID to fetch the backend conversation history
    const conversationId = demoResponse.conversation_id;
    
    if (!conversationId) return;

    try {
      // Fetch the full conversation history from backend
      const response = await fetch(getApiUrl(`/api/conversation-info/${conversationId}`));
      if (!response.ok) {
        throw new Error('Failed to fetch conversation history');
      }
      
      // For now, let's create a step-by-step conversation manually
      // This simulates the realistic conversation flow
      await simulateTypingConversation(demoResponse);
      
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      // Fallback to manual simulation
      await simulateTypingConversation(demoResponse);
    }
  }, [simulateTypingConversation]);

  // Get quotation completion percentage
  const getQuotationProgress = useCallback(() => {
    const completion = conversation.dataCompletion;
    const match = completion.match(/(\d+)\/(\d+)/);
    if (match) {
      const completed = parseInt(match[1]);
      const total = parseInt(match[2]);
      return Math.round((completed / total) * 100);
    }
    return 0;
  }, [conversation.dataCompletion]);

  // Check if quotation is complete
  const isQuotationComplete = useCallback(() => {
    return getQuotationProgress() === 100;
  }, [getQuotationProgress]);

  // Get missing quotation fields
  const getMissingQuotationFields = useCallback((): string[] => {
    const data = conversation.quotationData;
    const missing: string[] = [];
    
    if (!data.customer_email) missing.push('Email');
    if (!data.company_name) missing.push('Company');
    if (!data.budget_per_pack && !data.total_budget) missing.push('Budget');
    if (!data.number_of_packs) missing.push('Quantity');
    if (!data.occasion) missing.push('Occasion');
    if (!data.special_requests) missing.push('Requirements');
    
    return missing;
  }, [conversation.quotationData]);

  // Initialize conversation on mount if autoStart is true
  useEffect(() => {
    if (autoStart && !conversation.id && conversation.messages.length === 0 && !initAttemptedRef.current) {
      initAttemptedRef.current = true;
      startNewConversation();
    }
  }, [autoStart, conversation.id, conversation.messages.length, startNewConversation]);

  return {
    conversation,
    error,
    sendMessage,
    uploadImage,
    startNewConversation,
    resetConversation,
    clearError,
    loadDemoConversation,
    getQuotationProgress,
    isQuotationComplete,
    getMissingQuotationFields,
    messagesEndRef,
    isLoading: conversation.isLoading,
  };
};